/**
 * timeSlotService — Firestore persistence for the `timeSlots` collection.
 *
 * Centralizes the reads/writes that previously lived inline in the four legacy
 * scheduling pages (now the unified Scheduling surface's Calendar / Slot Editor
 * / Visual tabs). Behavior is unchanged: callers build the exact document shape
 * (with their own Timestamps) and these helpers perform the raw Firestore op by
 * id, so writes target the same documents as `updateDoc(doc.ref, …)` did.
 */

import { db } from '../firebase';
import {
	collection,
	doc,
	query,
	where,
	getDocs,
	addDoc,
	updateDoc,
	deleteDoc,
	deleteField,
	writeBatch,
	Timestamp,
} from 'firebase/firestore';

const COLLECTION = 'timeSlots';

// Assigned slots only (read view — the Calendar tab).
export async function fetchAssignedTimeSlots() {
	const q = query(collection(db, COLLECTION), where('status', '==', 'assigned'));
	const snapshot = await getDocs(q);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// A set of students' CONFIRMED slots (customer schedule view — Slice B).
//
// BOTH constraints are load-bearing, not optimizations — rules are not filters,
// so a query that reaches for a doc the rule won't authorize is denied WHOLE,
// not silently trimmed:
//   studentId-in  — the customer rule only authorizes slots whose student
//                   belongs to the caller's family.
//   status ==     — the rule now also excludes provisional carryover holds
//     'assigned'    (last year's student pencilled into this year's grid while
//                   Grace works through the calls). Those carry a real
//                   studentId, so WITHOUT this constraint a family with one
//                   unconfirmed hold would have their entire schedule read
//                   denied — not just that row.
//
// The (studentId, status) composite index this needs already exists in
// firestore.indexes.json. (An earlier comment here claimed status could not be
// queried without adding one; the index has been there since the pool work.)
// Chunked to Firestore's 30-value `in` cap (families are 1–4 kids; belt only).
export async function fetchTimeSlotsForStudents(studentIds) {
	const ids = (studentIds || []).filter(Boolean);
	if (ids.length === 0) return [];
	const chunks = [];
	for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
	const snapshots = await Promise.all(
		chunks.map((chunk) =>
			getDocs(query(
				collection(db, COLLECTION),
				where('studentId', 'in', chunk),
				where('status', '==', 'assigned')
			))
		)
	);
	return snapshots.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })));
}

// All slots (Slot Editor + Visual). Sorting/filtering stays with the caller to
// preserve the existing per-surface behavior.
export async function fetchAllTimeSlots() {
	const snapshot = await getDocs(query(collection(db, COLLECTION)));
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addTimeSlot(data) {
	return addDoc(collection(db, COLLECTION), data);
}

export async function updateTimeSlot(id, data) {
	return updateDoc(doc(db, COLLECTION, id), data);
}

export async function deleteTimeSlot(id) {
	return deleteDoc(doc(db, COLLECTION, id));
}

// Batch delete (Slot Editor bulk actions). Chunked to Firestore's 500-op batch
// cap. Admin-only at the rules layer (`allow delete: if isAdmin()`), same as
// deleteTimeSlot — a batch is evaluated per op, so a non-admin caller is denied
// server-side exactly as the per-row path is. Each chunk is atomic on its own;
// a failure mid-run leaves earlier chunks committed (callers say so).
export async function deleteTimeSlots(ids) {
	const list = (ids || []).filter(Boolean);
	for (let i = 0; i < list.length; i += 500) {
		const batch = writeBatch(db);
		list.slice(i, i + 500).forEach((id) => batch.delete(doc(db, COLLECTION, id)));
		await batch.commit();
	}
}

// Batch status update (bulk block / unblock) — the same op the per-row toggle
// issues, batched, under the same update rule (admins/owners/viewSchedule
// staff). Stamps updatedAt to match the single-doc write path.
export async function updateTimeSlotsStatus(ids, status) {
	const list = (ids || []).filter(Boolean);
	for (let i = 0; i < list.length; i += 500) {
		const batch = writeBatch(db);
		list.slice(i, i + 500).forEach((id) =>
			batch.update(doc(db, COLLECTION, id), { status, updatedAt: Timestamp.now() })
		);
		await batch.commit();
	}
}

// ---- provisional carryover holds -------------------------------------------
//
// A hold is last year's student pencilled into this year's grid while Grace
// works through the calls. It names them in holdStudentId/holdStudentName and
// leaves studentId NULL, because studentId means "booked" — see the header of
// scripts/migration/importFallSlotGrid2627.js.
//
// These are separate from updateTimeSlotsStatus, which only writes `status`:
// confirming and releasing both have to move the hold fields too, and a partial
// write would leave a slot that is assigned to nobody or open but still held.

// The write that turns a hold into a real booking: holdStudentId becomes
// studentId. Mirrors the audit shape performAssignStudentToSlot writes
// (functions/registration.js), because this IS an assignment.
export function confirmHoldPatch(slot, actorUid) {
	return {
		studentId: slot.holdStudentId,
		studentName: slot.holdStudentName || null,
		status: 'assigned',
		holdStudentId: null,
		holdStudentName: null,
		holdSource: null,
		holdSetAt: null,
		assignedAt: Timestamp.now(),
		assignedBy: actorUid || null,
		assignedVia: 'fall-2627-carryover',
		updatedAt: Timestamp.now(),
	};
}

// ---- returning a slot to the pool -----------------------------------------
//
// A timeSlots doc OUTLIVES the booking it holds. The same document is assigned,
// released, and assigned again to a different child, so anything written onto it
// on behalf of ONE student has to come off when that student lets go — otherwise
// it is inherited by the next one.
//
// `exceptions` is exactly that kind of field. It is per-STUDENT state living on
// a SHARED doc: makeupCredits.js writes `exceptions.<date> = {cancelled, note}`
// as the durable record of one student's cancelled occurrence (see its "the
// exception key IS the durable cancellation record" comment), and
// occurrenceExpansion.expandOnto SUPPRESSES any date carrying `cancelled: true`.
// So a stale entry does not merely look untidy on the next student's schedule —
// their lesson on that date silently does not render, and the free-text note
// (a family's own words about their child) rides along with it.
//
// Measured in prod 2026-07-29: `s-2026-2027-0-1615-HLDC` sat in the open
// /register pool carrying two cancellations from the student who had just been
// unassigned from it, notes included. Whoever booked that Sunday next would have
// lost two lessons they had paid for, with no surface anywhere saying why.
//
// `cancellationNote` is the same shape of hazard (makeupCredits.js sets it on an
// inside-24h block, and its own comment explains that an OPEN doc is readable by
// every signed-in customer, so a note left on one leaks between families).
//
// Deliberately NOT cleared here: `notes`, and the assignment audit stamps
// (`assignedAt`/`assignedBy`/`assignedVia`/`sourceRequestId`). `notes` is
// sometimes STUDIO metadata rather than student text — the live grid has
// "Triple Threat group class" sitting in it — so a blanket wipe would destroy
// scheduling information; the two unassign paths clear it because a student's
// lesson notes are what they hold there, and that behavior is unchanged. The
// audit stamps are history, nothing reads them, and losing "this was once
// assigned via /register" makes the trail worse rather than safer.
export function studentResiduePatch() {
	return {
		exceptions: {},
		cancellationNote: deleteField(),
	};
}

// The write that returns an ASSIGNED slot to "open". Shared by the two surfaces
// that offer Unassign — the Slot Editor's admin escape hatch and the student
// profile's per-slot button — which issued byte-identical inline patches before
// this existed. One of them having been fixed and the other not is precisely how
// the residue bug above would come back.
export function unassignSlotPatch() {
	return {
		studentId: null,
		studentName: null,
		status: 'open',
		isMakeup: false,
		notes: '',
		...studentResiduePatch(),
		updatedAt: Timestamp.now(),
	};
}

// The write that lets the slot go: back to the public pool, hold cleared.
export function releaseHoldPatch() {
	return {
		studentId: null,
		studentName: null,
		status: 'open',
		isMakeup: false,
		holdStudentId: null,
		holdStudentName: null,
		holdSource: null,
		holdSetAt: null,
		...studentResiduePatch(),
		updatedAt: Timestamp.now(),
	};
}

export async function confirmProvisionalHolds(slots, actorUid) {
	const list = (slots || []).filter((s) => s && s.id && s.holdStudentId);
	for (let i = 0; i < list.length; i += 500) {
		const batch = writeBatch(db);
		list.slice(i, i + 500).forEach((s) =>
			batch.update(doc(db, COLLECTION, s.id), confirmHoldPatch(s, actorUid))
		);
		await batch.commit();
	}
	return list.length;
}

export async function releaseProvisionalHolds(ids) {
	const list = (ids || []).filter(Boolean);
	for (let i = 0; i < list.length; i += 500) {
		const batch = writeBatch(db);
		list.slice(i, i + 500).forEach((id) => batch.update(doc(db, COLLECTION, id), releaseHoldPatch()));
		await batch.commit();
	}
	return list.length;
}
